import {
  Box,
  Button,
  Heading,
  Icon,
  InlineStack,
  Link,
  Paragraph,
  Stack,
  Text,
} from '@customerio/pluma-components/react';
import { PlatformIcon, PLATFORM_LABELS, type PushPlatform } from '@/features/push-credentials';
import { ViewerRestrictedTooltip } from '@/components/overlays/viewer-restricted-tooltip';

const COPY = {
  ios: {
    heading: 'Send iOS push notifications',
    description:
      'Send push notifications to customers with Apple devices using Apple Push Notification Service (APNs) or Firebase Cloud Messaging (FCM).',
    help: "For APNs, you'll need your .p8 certificate, Key ID, Bundle ID and Team ID. Learn more about ",
    linkText: 'sending iOS push notifications',
    linkHref: 'https://docs.customer.io/journeys/push-developer-guide/#apns-setup',
  },
  android: {
    heading: 'Send Android push notifications',
    description: 'Send push notifications to customers with Android devices using Firebase Cloud Messaging (FCM).',
    help: "You'll need to enable the FCM API for your project and generate a .json server key. Learn more about ",
    linkText: 'sending Android push notifications',
    linkHref: 'https://docs.customer.io/journeys/push-developer-guide/#fcm-setup',
  },
} satisfies Record<
  PushPlatform,
  { heading: string; description: string; help: string; linkText: string; linkHref: string }
>;

export type PushPlatformEmptyStateProps = {
  platform: PushPlatform;
  canCreate: boolean;
  onStartConfiguring: () => void;
};

export function PushPlatformEmptyState({ platform, canCreate, onStartConfiguring }: PushPlatformEmptyStateProps) {
  const copy = COPY[platform];

  return (
    <Stack alignInline="center" pt="300" data-test-push-empty-state={platform}>
      <PlatformIcon platform={platform} size="xl" mb="200" />
      <Heading level="2" mb="100">
        {copy.heading}
      </Heading>
      <Paragraph mb="300">{copy.description}</Paragraph>
      <Box mb="300">
        <ViewerRestrictedTooltip readOnly={!canCreate}>
          {(context) => (
            <Button
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              isDisabled={!canCreate}
              variant="primary"
              onClick={onStartConfiguring}
            >
              Enable push for {PLATFORM_LABELS[platform]}
            </Button>
          )}
        </ViewerRestrictedTooltip>
      </Box>
      <InlineStack mt="150" alignBlock="center">
        <Icon name="help" color="subtle" mr="100" />
        <Text color="subtle" size="xs">
          {copy.help}
          <Link href={copy.linkHref} variant="secondary" isExternal={true}>
            {copy.linkText}
          </Link>
          .
        </Text>
      </InlineStack>
    </Stack>
  );
}
